import { FC, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { paths } from 'src/paths';
import { useAuth } from 'src/hooks/use-auth';
import { useAllOrganizations } from 'src/hooks/use-all-organizations';

interface OrganizationGuardProps {
  children: ReactNode;
}

export const OrganizationGuard: FC<OrganizationGuardProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { organizations } = useAllOrganizations();

  // Don't redirect while still loading user data or organizations
  const isUserLoading = !isAuthenticated || !user || user.groups === undefined;
  const isOrganizationsLoading = !organizations || organizations.length === 0;

  if (isUserLoading || isOrganizationsLoading) {
    console.log('🔄 ORG GUARD: Loading data, preventing redirect', {
      isAuthenticated,
      hasUser: !!user,
      userGroups: user?.groups?.length || 0,
      organizationsCount: organizations?.length || 0,
    });
    return <div>Loading...</div>;
  }

  // Check if user has any organization roles by matching against actual organization roles
  const userGroups = user?.groups || [];
  const organizationRoles = organizations.map((org) => org.role);
  const hasOrganizationAccess = userGroups.some((group) => organizationRoles.includes(group));

  if (!hasOrganizationAccess) {
    console.log('❌ ORG GUARD: User lacks organization-level permissions, redirecting', {
      userGroups,
      organizationRoles,
      hasOrganizationAccess,
      matchingRoles: userGroups.filter((g) => organizationRoles.includes(g)),
    });
    return <Navigate to={paths.dashboard.index} replace />;
  }

  console.log('✅ ORG GUARD: User has organization permissions, allowing access', {
    userGroups,
    organizationRoles,
    matchingRoles: userGroups.filter((g) => organizationRoles.includes(g)),
  });
  return <>{children}</>;
};
